import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Button, Icons, Notification } from '@/js/libs/ui/src/V2';

import { SearchFilter } from 'src/components/TableFilters/components/SearchFilter';
import { DEV_MODE } from 'src/constants';
import { useTranslateModeContext } from './context';
import { useTranslationData } from './hooks/useTranslationData';
import { EmptyState, OverlayContainer, SearchContainer, SectionContainer, ToggleButton, ToggleIcon, TranslationList } from './styled';
import { TranslationEditor } from './TranslationEditor';
import { TranslationListItem } from './TranslationListItem';
import type { TrackedTranslation } from './types';

export const TranslateModeOverlay = memo(function TranslateModeOverlay() {
  const [t] = useTranslation(['common']);
  const theme = useTheme();
  const { isEnabled, isOverlayOpen, trackedKeys, openOverlay, closeOverlay, editorModalState } = useTranslateModeContext();

  const [searchQuery, setSearchQuery] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const { translations } = useTranslationData(trackedKeys);

  const filteredTranslations = useMemo(() => {
    if (!searchQuery.trim()) {
      return translations;
    }

    const query = searchQuery.toLowerCase();
    return translations.filter((item) => item.fullKey.toLowerCase().includes(query) || item.value.toLowerCase().includes(query));
  }, [translations, searchQuery]);

  const handleTranslationClick = useCallbackRef((translation: TrackedTranslation) => {
    closeOverlay();
    editorModalState.open(translation);
  });

  const handleButtonClick = useCallbackRef(() => {
    if (isOverlayOpen) {
      closeOverlay();
    } else {
      setSearchQuery('');
      openOverlay();
    }
  });

  const handleClickOutside = useCallbackRef((e: MouseEvent) => {
    if (!isOverlayOpen) {
      return;
    }

    const target = e.target as Node;
    if (!overlayRef.current?.contains(target) && !toggleRef.current?.contains(target)) {
      closeOverlay();
    }
  });

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  return isEnabled
    ? createPortal(
        <>
          <ToggleButton ref={toggleRef} $isActive={isOverlayOpen} onClick={handleButtonClick} data-testid='translate-mode-toggle'>
            <ToggleIcon>T</ToggleIcon>
          </ToggleButton>

          <CSSTransition in={isOverlayOpen} timeout={200} unmountOnExit>
            <OverlayContainer ref={overlayRef} $visible={isOverlayOpen} data-testid='translate-mode-overlay'>
              <SectionContainer
                title={t('common:TRANSLATIONS')}
                tag={trackedKeys.length.toString()}
                actions={
                  <Button ghost iconOnly onClick={closeOverlay} data-testid='close-overlay-button'>
                    <Icons.Close size={20} color={theme.colors.grey100_1} />
                  </Button>
                }>
                {DEV_MODE && (
                  <SearchContainer>
                    <Notification type='warning' body={t('common:DEV_ENV_ALERT_MESSAGE')} fullWidth uncloseable />
                  </SearchContainer>
                )}
                <SearchContainer>
                  <SearchFilter fullWidth filter={searchQuery} onChange={setSearchQuery} />
                </SearchContainer>

                <TranslationList>
                  {filteredTranslations.length === 0 ? (
                    <EmptyState>{trackedKeys.length === 0 ? t('common:NO_DATA') : t('common:NO_MATCHES_FOUND')}</EmptyState>
                  ) : (
                    filteredTranslations.map((translation) => (
                      <TranslationListItem key={translation.fullKey} translation={translation} onClick={handleTranslationClick} />
                    ))
                  )}
                </TranslationList>
              </SectionContainer>
            </OverlayContainer>
          </CSSTransition>

          <TranslationEditor />
        </>,
        document.body,
      )
    : null;
});
